precision highp float;

// "freqX", 14e-5),
// "freqY", 29e-5)
varying float vTest;
uniform vec2 resolution;
uniform vec3 uColor1;
uniform vec3 uColor2;
uniform vec3 uColor3;
uniform vec3 uColor4;
uniform vec3 uColor5;
uniform float uAmp;
uniform float aspectRatio;
uniform float time;
uniform float uSpeed;
uniform float u_time;
uniform float u_shadow_power;
uniform float u_darken_top;
uniform vec4 u_active_colors;

const int u_waveLayers_length = 3;
              //
// Description : Array and textureless GLSL 2D/3D/4D simplex
//               noise functions.
//      Author : Ian McEwan, Ashima Arts.
//  Maintainer : stegu
//     Lastmod : 20110822 (ijm)
//     License : Copyright (C) 2011 Ashima Arts. All rights reserved.
//               Distributed under the MIT License. See LICENSE file.
//               https://github.com/ashima/webgl-noise
//               https://github.com/stegu/webgl-noise
//

vec3 mod289(vec3 x) {
    return x - floor(x * (1.0 / 289.0)) * 289.0;
}

vec4 mod289(vec4 x) {
    return x - floor(x * (1.0 / 289.0)) * 289.0;
}

vec4 permute(vec4 x) {
    return mod289(((x * 34.0) + 1.0) * x);
}

vec4 taylorInvSqrt(vec4 r) {
    return 1.79284291400159 - 0.85373472095314 * r;
}

float snoise(vec3 v) {
    const vec2 C = vec2(1.0 / 6.0, 1.0 / 3.0);
    const vec4 D = vec4(0.0, 0.5, 1.0, 2.0);

// First corner
    vec3 i = floor(v + dot(v, C.yyy));
    vec3 x0 = v - i + dot(i, C.xxx);

// Other corners
    vec3 g = step(x0.yzx, x0.xyz);
    vec3 l = 1.0 - g;
    vec3 i1 = min(g.xyz, l.zxy);
    vec3 i2 = max(g.xyz, l.zxy);

  //   x0 = x0 - 0.0 + 0.0 * C.xxx;
  //   x1 = x0 - i1  + 1.0 * C.xxx;
  //   x2 = x0 - i2  + 2.0 * C.xxx;
  //   x3 = x0 - 1.0 + 3.0 * C.xxx;
    vec3 x1 = x0 - i1 + C.xxx;
    vec3 x2 = x0 - i2 + C.yyy; // 2.0*C.x = 1/3 = C.y
    vec3 x3 = x0 - D.yyy;      // -1.0+3.0*C.x = -0.5 = -D.y

// Permutations
    i = mod289(i);
    vec4 p = permute(permute(permute(i.z + vec4(0.0, i1.z, i2.z, 1.0)) + i.y + vec4(0.0, i1.y, i2.y, 1.0)) + i.x + vec4(0.0, i1.x, i2.x, 1.0));

// Gradients: 7x7 points over a square, mapped onto an octahedron.
// The ring size 17*17 = 289 is close to a multiple of 49 (49*6 = 294)
    float n_ = 0.142857142857; // 1.0/7.0
    vec3 ns = n_ * D.wyz - D.xzx;

    vec4 j = p - 49.0 * floor(p * ns.z * ns.z);  //  mod(p,7*7)

    vec4 x_ = floor(j * ns.z);
    vec4 y_ = floor(j - 7.0 * x_);    // mod(j,N)

    vec4 x = x_ * ns.x + ns.yyyy;
    vec4 y = y_ * ns.x + ns.yyyy;
    vec4 h = 1.0 - abs(x) - abs(y);

    vec4 b0 = vec4(x.xy, y.xy);
    vec4 b1 = vec4(x.zw, y.zw);

  //vec4 s0 = vec4(lessThan(b0,0.0))*2.0 - 1.0;
  //vec4 s1 = vec4(lessThan(b1,0.0))*2.0 - 1.0;
    vec4 s0 = floor(b0) * 2.0 + 1.0;
    vec4 s1 = floor(b1) * 2.0 + 1.0;
    vec4 sh = -step(h, vec4(0.0));

    vec4 a0 = b0.xzyw + s0.xzyw * sh.xxyy;
    vec4 a1 = b1.xzyw + s1.xzyw * sh.zzww;

    vec3 p0 = vec3(a0.xy, h.x);
    vec3 p1 = vec3(a0.zw, h.y);
    vec3 p2 = vec3(a1.xy, h.z);
    vec3 p3 = vec3(a1.zw, h.w);

//Normalise gradients
    vec4 norm = taylorInvSqrt(vec4(dot(p0, p0), dot(p1, p1), dot(p2, p2), dot(p3, p3)));
    p0 *= norm.x;
    p1 *= norm.y;
    p2 *= norm.z;
    p3 *= norm.w;

// Mix final noise value
    vec4 m = max(0.6 - vec4(dot(x0, x0), dot(x1, x1), dot(x2, x2), dot(x3, x3)), 0.0);
    m = m * m;
    return 42.0 * dot(m * m, vec4(dot(p0, x0), dot(p1, x1), dot(p2, x2), dot(p3, x3)));
}

//
// https://github.com/jamieowen/glsl-blend
//

// Normal

vec3 blendNormal(vec3 base, vec3 blend) {
    return blend;
}

vec3 blendNormal(vec3 base, vec3 blend, float opacity) {
    return (blendNormal(base, blend) * opacity + base * (1.0 - opacity));
}



varying vec3 v_color;

void main() {
    
    vec2 uvNorm = uv;
    // float time = u_time *;

    vec2 noiseCoord =  uv * vec2(3.,4.)*0.35;

    vec2 st = 1. - uv.xy;


  //
  // Tilting the plane
  //

  // Front-to-back tilt
    float tilt = -1. / 2.0 * uvNorm.y;

// u_vertDeform
//========
vec2 noiseFreq = vec2(3.,4.);
float noiseFlow = 3.;
float offsetBottom = -.5;
float offsetTop = -.5;
float noiseSpeed = 10.;
float noiseincline = 0.;
float noiseAmp =uAmp;
float noiseSeed = 10.;



  // Left-to-right angle
    float incline = 1. * uv.x / 2.0 * noiseincline;

  // Up-down shift to offset incline
    float offset = 1. / 2.0 * incline * mix(offsetBottom, offsetTop, uv.y);

  //
  // Vertex noise
  //

    float noise = snoise(vec3(noiseCoord.x * noiseFreq.x + time * noiseFlow, noiseCoord.y * noiseFreq.y, time * noiseSpeed + noiseSeed)) * noiseAmp;

  // Fade noise to zero at edges
    noise *= 1.0 - pow(abs(uvNorm.y), 2.0);
    vTest = noise;

  // Clamp to 0
    noise = max(0.0, noise);

    vec3 pos = vec3(position.x, position.y , position.z + tilt + incline + noise - offset);

  //
  // Vertex color, to be passed to fragment shader
  //

    // if(u_active_colors[0] == 1.) {
    //     v_color = u_baseColor;
    // }

//     const vec3 palette[5] = vec3[5](
//   uColor1,
//   uColor2,
//   uColor3,
//   uColor4,
//   uColor5
// );
v_color =uColor1;
    for(int i = 0; i < 5; i++) {
            // WaveLayers layer = u_waveLayers[i];
            float ind = float(i);
            float noiseSpeed = 11. + .3 * ind;
            float noiseFlow = 6.5 + .3 * ind;
            float noiseSeed = 10.*ind;
            float noiseFloor = 0.1;
            float noiseCeil =.63 + .07 * ind;
            vec3 noisecolor = uColor1;
            if(i==0){noisecolor = uColor1;}
            if(i==1){noisecolor = uColor2;}
            if(i==2){noisecolor = uColor3;}
            if(i==3){noisecolor = uColor4;}
            if(i==4){noisecolor = uColor5;}
            // vec3 noisecolor = palette[i];

            vec2 noiseFreq = vec2(
                 2. + ind / 5., 
                 3. + ind / 5.
            );
            

            float noise = smoothstep(noiseFloor, noiseCeil, 
            snoise(vec3(noiseCoord.x * noiseFreq.x + uSpeed*time * noiseFlow, noiseCoord.y * noiseFreq.y, uSpeed*time * noiseSpeed + noiseSeed)) / 2.0 + 0.5
            );

            v_color = blendNormal(v_color, noisecolor, pow(noise, 4.));
    }



    gl_Position = projectionMatrix * modelViewMatrix * vec4(pos, 1.0);
}